import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { authorizationApi } from '../../services/authorizationApiService';
import PageHeader from '../common/PageHeader/PageHeader';
import Select from '../common/Select/Select';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import './UsageLimits.css';

// ─── Shared: Model Limits Editor ─────────────────────────────────────────────
function ModelLimitsEditor({ modelLimits, availableModels, onChange }) {
  const [newModelKey, setNewModelKey] = useState('');

  const addModelLimit = () => {
    if (!newModelKey || !newModelKey.includes('/')) {
      alert('Please select a valid model from the dropdown.');
      return;
    }
    if (modelLimits[newModelKey]) {
      alert('Limit for this model already exists.');
      return;
    }
    onChange({ ...modelLimits, [newModelKey]: { tokenLimit: null, costLimit: null } });
    setNewModelKey('');
  };

  const removeModelLimit = (key) => {
    const updated = { ...modelLimits };
    delete updated[key];
    onChange(updated);
  };

  const updateModelLimit = (key, field, value) => {
    onChange({
      ...modelLimits,
      [key]: {
        ...modelLimits[key],
        [field]: value === '' ? null : (field === 'tokenLimit' ? parseInt(value, 10) : parseFloat(value))
      }
    });
  };

  return (
    <div className="model-limits-area">
      <h4>Per-Model Limits</h4>
      <div className="model-add-row">
        {availableModels && Object.keys(availableModels).length > 0 ? (
          <Select
            value={newModelKey}
            onChange={(e) => setNewModelKey(e.target.value)}
            placeholder="Select a model to add..."
            options={Object.entries(availableModels).flatMap(([provider, models]) =>
              models.map((m) => ({
                value: `${provider}/${m.id}`,
                label: `${provider}/${m.name || m.id}`
              }))
            )}
          />
        ) : (
          <input
            type="text"
            value={newModelKey}
            onChange={(e) => setNewModelKey(e.target.value)}
            placeholder="provider/model (e.g. openai/gpt-4o)"
          />
        )}
        <button className="limits-btn limits-btn-secondary" onClick={addModelLimit}>
          <i className="fas fa-plus"></i> Add Model
        </button>
      </div>

      {Object.keys(modelLimits).map(key => (
        <div key={key} className="model-limit-row">
          <div className="model-limit-name">{key}</div>
          <div className="model-limit-input">
            <input
              type="number"
              value={modelLimits[key].tokenLimit || ''}
              onChange={(e) => updateModelLimit(key, 'tokenLimit', e.target.value)}
              placeholder="Max Tokens"
            />
          </div>
          <div className="model-limit-input">
            <input
              type="number"
              step="0.01"
              value={modelLimits[key].costLimit || ''}
              onChange={(e) => updateModelLimit(key, 'costLimit', e.target.value)}
              placeholder="Max Cost ($)"
            />
          </div>
          <button className="limits-btn-danger" onClick={() => removeModelLimit(key)} title="Remove">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Section 1: Organization Defaults ────────────────────────────────────────
function OrganizationLimitsSection({ apiClient, availableModels }) {
  const [maxTokens, setMaxTokens] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [isEnforcementEnabled, setIsEnforcementEnabled] = useState(false);
  const [modelLimits, setModelLimits] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadLimits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.request('/api/metrics/organization/limits');
      setMaxTokens(data.maxTokensPerUser !== null && data.maxTokensPerUser !== undefined ? data.maxTokensPerUser : '');
      setMaxCost(data.maxCostPerUser !== null && data.maxCostPerUser !== undefined ? data.maxCostPerUser : '');
      setIsEnforcementEnabled(data.isEnforcementEnabled || false);
      setModelLimits(data.modelLimits || {});
    } catch (err) {
      console.error('Error loading organization limits:', err);
      setError('Failed to load organization limits.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => { loadLimits(); }, [loadLimits]);

  const saveLimits = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = {
        max_tokens_per_user: maxTokens === '' ? null : parseInt(maxTokens, 10),
        max_cost_per_user: maxCost === '' ? null : parseFloat(maxCost),
        is_enforcement_enabled: isEnforcementEnabled,
        model_limits: modelLimits
      };
      await apiClient.request('/api/metrics/organization/limits', {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving organization limits:', err);
      setError('Failed to save organization limits.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="limits-loading"><i className="fas fa-spinner fa-spin"></i> Loading organization limits...</div>;
  }

  return (
    <div className="limits-section-card">
      <div className="limits-section-header">
        <i className="fas fa-building"></i>
        <h3>Organization Defaults</h3>
      </div>
      <p className="limits-section-desc">
        Set default caps on token and cost consumption per user across the organization. These apply to all users unless overridden by role or individual limits.
      </p>

      {error && <div className="limits-alert error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      {success && <div className="limits-alert success"><i className="fas fa-check-circle"></i> Organization limits updated!</div>}

      <div className="limits-form-row">
        <div className="limits-form-group">
          <label>Max Tokens per User (Monthly)</label>
          <input type="number" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="e.g. 100000" />
        </div>
        <div className="limits-form-group">
          <label>Max Cost per User (USD, Monthly)</label>
          <input type="number" step="0.01" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} placeholder="e.g. 10.00" />
        </div>
      </div>

      <div className="limits-toggle">
        <ToggleSwitch
          checked={isEnforcementEnabled}
          onChange={setIsEnforcementEnabled}
          label="Enable Hard Enforcement (Block requests if exceeded)"
        />
      </div>

      <ModelLimitsEditor
        modelLimits={modelLimits}
        availableModels={availableModels}
        onChange={setModelLimits}
      />

      <button className="limits-btn limits-btn-primary" onClick={saveLimits} disabled={saving}>
        {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
        Apply Organization Limits
      </button>
    </div>
  );
}

// ─── Section 2: Role Limits ──────────────────────────────────────────────────
function RoleLimitsSection({ apiClient, availableModels }) {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [modelLimits, setModelLimits] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const data = await authorizationApi.getRoles();
      const fetchedRoles = data?.roles || [];

      // Fetch limits for each role to show indicators
      const rolesWithLimits = await Promise.all(fetchedRoles.map(async (r) => {
        try {
          const limitData = await apiClient.request(`/api/metrics/roles/${r.role_id}/limits`);
          return { ...r, hasLimits: true, limitData };
        } catch (err) {
          return { ...r, hasLimits: false };
        }
      }));
      setRoles(rolesWithLimits);
    } catch (err) {
      console.error('Error loading roles:', err);
    } finally {
      setLoadingRoles(false);
    }
  }, [apiClient]);

  const resetForm = useCallback(() => {
    setMaxTokens('');
    setMaxCost('');
    setModelLimits({});
  }, []);

  const loadRoleLimits = useCallback(async (roleId) => {
    setLoadingLimits(true);
    setError(null);
    try {
      const data = await apiClient.request(`/api/metrics/roles/${roleId}/limits`);
      setMaxTokens(data.tokenLimit !== null && data.tokenLimit !== undefined ? data.tokenLimit : '');
      setMaxCost(data.costLimit !== null && data.costLimit !== undefined ? data.costLimit : '');
      setModelLimits(data.modelLimits || {});
    } catch (err) {
      // If 404, role has no limits yet — that's fine
      if (err.message?.includes('404') || err.status === 404) {
        resetForm();
      } else {
        console.error('Error loading role limits:', err);
        setError('Failed to load role limits.');
      }
    } finally {
      setLoadingLimits(false);
    }
  }, [apiClient, resetForm]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (selectedRoleId) loadRoleLimits(selectedRoleId);
    else resetForm();
  }, [selectedRoleId, loadRoleLimits, resetForm]);

  const saveLimits = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = {
        token_limit: maxTokens === '' ? null : parseInt(maxTokens, 10),
        cost_limit: maxCost === '' ? null : parseFloat(maxCost),
        model_limits: modelLimits
      };
      await apiClient.request(`/api/metrics/roles/${selectedRoleId}/limits`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving role limits:', err);
      setError('Failed to save role limits.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="limits-section-card">
      <div className="limits-section-header">
        <i className="fas fa-user-tag"></i>
        <h3>Role-Based Limits</h3>
      </div>
      <p className="limits-section-desc">
        Set limits per role. Users inherit the limits of their assigned role, unless they have individual overrides.
        Priority: User Override → Role Limit → Organization Default.
      </p>

      {error && <div className="limits-alert error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      {success && <div className="limits-alert success"><i className="fas fa-check-circle"></i> Role limits updated!</div>}

      <div className="entity-selector">
        <label>Select Role</label>
        {loadingRoles ? (
          <div className="limits-loading"><i className="fas fa-spinner fa-spin"></i> Loading roles...</div>
        ) : roles.length === 0 ? (
          <div className="entity-empty">No roles found. Create roles in the Authorization tab first.</div>
        ) : (
          <Select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            options={[
              { value: '', label: 'Choose a role...' },
              ...roles.map((r) => ({
                value: r.role_id,
                label: `${r.role_name}${r.hasLimits ? ' (Limits Configured)' : ''}`
              }))
            ]}
          />
        )}
      </div>

      {selectedRoleId && (
        loadingLimits ? (
          <div className="limits-loading"><i className="fas fa-spinner fa-spin"></i> Loading role limits...</div>
        ) : (
          <>
            <div className="limits-form-row">
              <div className="limits-form-group">
                <label>Max Tokens (Monthly)</label>
                <input type="number" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="e.g. 100000" />
              </div>
              <div className="limits-form-group">
                <label>Max Cost (USD, Monthly)</label>
                <input type="number" step="0.01" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} placeholder="e.g. 10.00" />
              </div>
            </div>

            <ModelLimitsEditor
              modelLimits={modelLimits}
              availableModels={availableModels}
              onChange={setModelLimits}
            />

            <button className="limits-btn limits-btn-primary" onClick={saveLimits} disabled={saving}>
              {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
              Apply Role Limits
            </button>
          </>
        )
      )}
    </div>
  );
}

// ─── Section 3: User Overrides ───────────────────────────────────────────────
function UserLimitsSection({ apiClient, availableModels }) {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [maxTokens, setMaxTokens] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [modelLimits, setModelLimits] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      // Use the metrics summary to get the list of users
      const now = new Date();
      const fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const data = await apiClient.request(`/api/metrics/users/summary?from_date=${fromDate}&to_date=${toDate}`);
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [apiClient]);

  const resetForm = useCallback(() => {
    setMaxTokens('');
    setMaxCost('');
    setModelLimits({});
    setSelectedUser(null);
  }, []);

  const loadUserLimits = useCallback(async (userId) => {
    setLoadingLimits(true);
    setError(null);
    try {
      const now = new Date();
      const fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const userDetail = await apiClient.getUserDetail(userId, fromDate, toDate);
      const limits = userDetail?.limits || {};
      setMaxTokens(limits.tokenLimit !== null && limits.tokenLimit !== undefined ? limits.tokenLimit : '');
      setMaxCost(limits.costLimit !== null && limits.costLimit !== undefined ? limits.costLimit : '');
      setModelLimits(limits.modelLimits || {});
      setSelectedUser(users.find(u => u.user_id === userId) || null);
    } catch (err) {
      console.error('Error loading user limits:', err);
      setError('Failed to load user limits.');
    } finally {
      setLoadingLimits(false);
    }
  }, [apiClient, users]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    if (selectedUserId) loadUserLimits(selectedUserId);
    else resetForm();
  }, [selectedUserId, loadUserLimits, resetForm]);

  const saveLimits = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = {
        token_limit: maxTokens === '' ? null : parseInt(maxTokens, 10),
        cost_limit: maxCost === '' ? null : parseFloat(maxCost),
        limit_period: 'monthly',
        alert_threshold: 0.8,
        notifications_enabled: true,
        model_limits: modelLimits
      };
      await apiClient.updateUserLimits(selectedUserId, payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving user limits:', err);
      setError('Failed to save user limits.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="limits-section-card">
      <div className="limits-section-header">
        <i className="fas fa-user-shield"></i>
        <h3>Individual User Overrides</h3>
      </div>
      <p className="limits-section-desc">
        Override limits for specific users. These take the highest priority, overriding both role and organization defaults. Leave blank to fall back to role/organization limits.
      </p>

      {error && <div className="limits-alert error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      {success && <div className="limits-alert success"><i className="fas fa-check-circle"></i> User limits updated!</div>}

      <div className="entity-selector">
        <label>Select User</label>
        {loadingUsers ? (
          <div className="limits-loading"><i className="fas fa-spinner fa-spin"></i> Loading users...</div>
        ) : users.length === 0 ? (
          <div className="entity-empty">No users found with usage data.</div>
        ) : (
          <Select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            options={[
              { value: '', label: 'Choose a user...' },
              ...users.map((u) => {
                const hasLimits = u.token_limit !== null || u.cost_limit !== null;
                return {
                  value: u.user_id,
                  label: `${u.display_name} (${u.email})${hasLimits ? ' (Overrides Configured)' : ''}`
                };
              })
            ]}
          />
        )}
      </div>

      {selectedUserId && (
        loadingLimits ? (
          <div className="limits-loading"><i className="fas fa-spinner fa-spin"></i> Loading user limits...</div>
        ) : (
          <>
            {selectedUser && (
              <div style={{ marginBottom: '16px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                Editing limits for <strong style={{ color: 'var(--text-primary)' }}>{selectedUser.display_name}</strong>
              </div>
            )}

            <div className="limits-form-row">
              <div className="limits-form-group">
                <label>Max Tokens (Monthly)</label>
                <input type="number" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="Leave blank for role/org default" />
              </div>
              <div className="limits-form-group">
                <label>Max Cost (USD, Monthly)</label>
                <input type="number" step="0.01" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} placeholder="Leave blank for role/org default" />
              </div>
            </div>

            <ModelLimitsEditor
              modelLimits={modelLimits}
              availableModels={availableModels}
              onChange={setModelLimits}
            />

            <button className="limits-btn limits-btn-primary" onClick={saveLimits} disabled={saving}>
              {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
              Apply User Limits
            </button>
          </>
        )
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function UsageLimits() {
  const { apiClient } = useApi();
  const [activeSection, setActiveSection] = useState('organization');
  const [availableModels, setAvailableModels] = useState({});

  const loadAvailableModels = useCallback(async () => {
    const providers = ['openai', 'anthropic'];
    const fetchModelsByProvider = async (provider) => {
      try {
        const response = await apiClient.getModels(provider, null);
        if (response.status === 'success' || response.status === 'partial') {
          return { provider, models: response.models || [] };
        }
      } catch (err) {
        console.error(`Error loading models for ${provider}:`, err);
      }
      return { provider, models: [] };
    };

    const results = await Promise.all(providers.map(fetchModelsByProvider));
    const combined = {};
    results.forEach(({ provider, models }) => {
      combined[provider] = models;
    });
    setAvailableModels(combined);
  }, [apiClient]);

  useEffect(() => {
    loadAvailableModels();
  }, [loadAvailableModels]);

  const sections = [
    { id: 'organization', label: 'Organization Defaults', icon: 'fas fa-building' },
    { id: 'roles', label: 'Role Limits', icon: 'fas fa-user-tag' },
    { id: 'users', label: 'User Overrides', icon: 'fas fa-user-shield' },
  ];

  return (
    <div className="page-container usage-limits-page">
      <div className="page-content">
        <PageHeader
          title="Usage Limits"
          description="Configure token and cost limits at organization, role, and individual user levels."
          icon="fas fa-gauge-high"
          accentColor="purple"
        />

        <div className="limits-subtabs">
          {sections.map(s => (
            <button
              key={s.id}
              className={`limits-subtab ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              <i className={s.icon}></i> {s.label}
            </button>
          ))}
        </div>

        {activeSection === 'organization' && (
          <OrganizationLimitsSection apiClient={apiClient} availableModels={availableModels} />
        )}
        {activeSection === 'roles' && (
          <RoleLimitsSection apiClient={apiClient} availableModels={availableModels} />
        )}
        {activeSection === 'users' && (
          <UserLimitsSection apiClient={apiClient} availableModels={availableModels} />
        )}
      </div>
    </div>
  );
}
